import { useEffect, useMemo, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Table,
  Text,
  TextField,
  TrashIcon,
  useFormField,
  useModalControl,
  useAlert,
  Modal,
  ModalControlModalProps,
  Tabs,
  NakedUl,
  SearchField,
  PrimaryButton,
  SpinningLoader,
  Heading,
  styles,
  TextLink,
} from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { getNameWithoutExtension } from '../../utils/phone-utils';
import { useAudioQuery } from '../../utils/use-audio-query';
import { AudioUploadModalContent, GenerateAudioModalComponent } from '../audio-picker/audio-upload-modal';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import { SettingsCard } from '../common/settings-card';
import { GroupedCallObjectUsage, VoicemailGreeting } from './types';
import { UsageDetails } from './usage-details';
import { useVoicemailBoxGreetingMutations } from './use-voicemail-box-mutations';

const GreetingsCardContent = ({
  tenantLocation,
  mailboxId,
  greetings = [],
  callObjectUsage,
  onGreetingChange,
}: {
  tenantLocation: SettingsTenantLocation;
  mailboxId: string;
  greetings?: VoicemailGreeting[];
  callObjectUsage: GroupedCallObjectUsage;
  onGreetingChange: (incoming: { id: string; number: number; name: string }) => void;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps, openModal } = useModalControl();
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();

  const hasPhoneFullAccess = hasSchemaACL(tenantLocation?.id ?? '', Permission.PHONE_FULL_ACCESS);

  const { remove } = useVoicemailBoxGreetingMutations(tenantLocation.phoneTenantId ?? '', mailboxId);

  const numberOptions = useMemo(() => {
    // We only show greeting numbers 0 and 1 for device boxes and 0-9 for general (non-device) boxes, however, if the
    // call routes feature flag is enabled, we show 0-9 for device boxes as well. Eventually, we will remove the devicebox distinction and show 0-9 for all boxes.
    return Array.from({ length: 10 }, (_, i) => {
      return {
        value: i.toString(),
        label: i.toString(),
        disabled: (i !== 0 && !!greetings?.find((d) => d.greetingNumber === i)) ?? false,
      };
    });
  }, [greetings]);

  return (
    <SettingsCard
      title={t('Greetings')}
      description={
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <Text>
            {t(
              'Empower non-administrators to control their voicemail greetings independently of call routing by assigning unique greeting numbers.'
            )}
          </Text>
          <Trans t={t}>
            <Text>
              After adding a greeting here, the greeting can be selected for this voicemail box in{' '}
              {hasPhoneFullAccess ? (
                <TextLink
                  onClick={() => {
                    navigate({ to: '/phone/call-routes' });
                  }}
                  trackingId={trackingId({
                    context: 'setting',
                    feature: 'voicemail-box',
                    details: 'call-routes-link',
                  })}
                >
                  Call Routes
                </TextLink>
              ) : (
                'Call Routes'
              )}
              .
            </Text>
          </Trans>
        </div>
      }
      action={{
        label: t('Add Greeting'),
        onClick: () => openModal(),
        ref: triggerProps.ref,
        trackingId: 'add-greeting',
      }}
    >
      <Table
        fullHeight
        fullHeightConfig={{
          maxHeight: 300,
          minHeight: 200,
        }}
        data={greetings}
        colConfig={[
          {
            id: 'name',
            accessor: (row) => row,
            Header: t('Name'),
            cellRenderer: (row: VoicemailGreeting) => (
              <div css={{ width: '100%' }}>
                <GreetingNameField greeting={row} onChange={onGreetingChange} />
              </div>
            ),
            sortType: (rowA, rowB) => {
              if (rowA.original.greetingName && rowB.original.greetingName) {
                return rowA.original.greetingName.localeCompare(rowB.original.greetingName);
              }

              return 0;
            },
          },
          {
            id: 'greetingNumber',
            accessor: (row) => row,
            Header: t('Greeting Number'),
            cellRenderer: (row: VoicemailGreeting) => {
              const options = numberOptions.reduce<{ value: string; label: string; disabled: boolean }[]>(
                (acc, option) => {
                  const label = option.label;

                  if (label) {
                    return [...acc, { ...option, label }];
                  }

                  return acc;
                },
                []
              );

              return (
                <div css={{ width: '100%' }}>
                  <GreetingNumberField options={options} greeting={row} onChange={onGreetingChange} />
                </div>
              );
            },
            sortType: (rowA, rowB) => {
              return rowA.original.greetingNumber - rowB.original.greetingNumber;
            },
          },
          {
            id: 'usage',
            accessor: (row) => row,
            width: 270,
            Header: t('Usage'),
            cellRenderer: (row: VoicemailGreeting) => {
              const greetingNumber = row.greetingNumber;
              const greetingNumberUsage = callObjectUsage[greetingNumber] ?? [];

              return <UsageDetails callObjectUsage={greetingNumberUsage} greetingNumber={row.greetingNumber} />;
            },
          },
          {
            id: 'lastUpdated',
            accessor: 'updatedAt',
            Header: t('Last Updated'),
            cellRenderer: (value) => {
              return value ? dayjs(value).format('LLL') : null;
            },
          },
          {
            id: 'greetingId',
            accessor: (row) => row,
            Header: t('Audio'),
            cellRenderer: (row) => <CachedAudioScrubber filePath={row.mediaPath} mediaId={row.greetingId} />,
            disableSortBy: true,
          },
        ]}
        rowActions={{
          actions: [
            {
              label: t('Delete'),
              Icon: TrashIcon,
              onClick: (row) => {
                remove.mutate(
                  { greetingId: row.greetingId, mailboxId: row.mailboxId },
                  {
                    onSuccess: () => {
                      alerts.success(t('Greeting deleted successfully.'));
                    },
                    onError: (error) => {
                      alerts.error(t('Failed to delete greeting.'));
                      logError('GreetingsCardContent:removeMutation | Failed to delete greeting', {
                        data: { mailboxId: row.mailboxId, greetingId: row.greetingId, error },
                      });
                    },
                  }
                );
              },
            },
          ],
        }}
      />
      <SelectGreetingModal modalProps={modalProps} mailboxId={mailboxId} tenantLocation={tenantLocation} />
    </SettingsCard>
  );
};

/**
 * If the incoming change conflicts with an existing greeting, we update the greeting number of the existing greeting to 0.
 *
 * Any number of greetings can be set to the greeting number 0.
 */
const getUpdatedGreetings = (
  greetings: VoicemailGreeting[],
  incoming: { id: string; number: number; name: string }
) => {
  return greetings.map((greeting) => {
    if (greeting.greetingId === incoming.id) {
      return { ...greeting, greetingNumber: incoming.number, greetingName: incoming.name };
    } else if (greeting.greetingNumber === incoming.number) {
      return { ...greeting, greetingNumber: 0 };
    }

    return greeting;
  });
};

export const GreetingsCard = ({
  tenantLocation,
  mailboxId,
  onChange,
  greetings,
  callObjectUsage,
}: {
  tenantLocation: SettingsTenantLocation;
  mailboxId: string;
  onChange: (incoming: VoicemailGreeting[]) => void;
  greetings: VoicemailGreeting[];
  callObjectUsage: GroupedCallObjectUsage;
}) => {
  const onGreetingChange = (incoming: { id: string; number: number; name: string }) => {
    const updatedGreetings = getUpdatedGreetings(greetings, incoming);

    onChange(updatedGreetings);
  };

  return (
    <GreetingsCardContent
      tenantLocation={tenantLocation}
      mailboxId={mailboxId}
      greetings={greetings}
      callObjectUsage={callObjectUsage}
      onGreetingChange={onGreetingChange}
    />
  );
};

const SelectGreetingModal = ({
  modalProps,
  mailboxId,
  tenantLocation,
}: {
  modalProps: ModalControlModalProps;
  mailboxId: string;
  tenantLocation: SettingsTenantLocation;
}) => {
  const { t } = useTranslation('phone');
  const { uploadAndCreateGreeting } = useVoicemailBoxGreetingMutations(tenantLocation.phoneTenantId ?? '', mailboxId);
  const [activeTab, setActiveTab] = useState('library');

  return (
    <Modal {...modalProps} minWidth={800}>
      <Modal.Header
        onClose={() => {
          // reset();
          modalProps.onClose();
        }}
      >
        {t('Add a Greeting')}
      </Modal.Header>
      <Modal.Body>
        <Tabs initialTab={activeTab} onChange={setActiveTab}>
          <Tabs.Bar css={{ marginBottom: theme.spacing(2) }}>
            <Tabs.Tab id='library'>{t('Audio Library')}</Tabs.Tab>
            <Tabs.Tab id='upload'>{t('Upload Audio')}</Tabs.Tab>
            <Tabs.Tab
              id='generate'
              trackingId={trackingId({
                context: 'setting',
                feature: 'voicemail-boxes',
                details: 'tts-generate-audio-tab',
              })}
            >
              <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
                {t('Generate Audio from Text')}{' '}
                <Icon css={{ color: theme.colors.primary20 }} name='aiassistant-small' />
              </div>
            </Tabs.Tab>
          </Tabs.Bar>
          <Tabs.Panel controller='library'>
            <AudioListTab tenantLocation={tenantLocation} mailboxId={mailboxId} modalProps={modalProps} />
          </Tabs.Panel>
          <Tabs.Panel controller='upload'>
            <AudioUploadModalContent
              customUploadFn={(payload) => {
                if (!tenantLocation.phoneTenantId) {
                  return;
                }

                return uploadAndCreateGreeting.mutate(
                  {
                    mailboxId,
                    greetingName: payload.fileName,
                    greetingNumber: 0,
                    file: payload.file,
                  },
                  {
                    onSettled: () => {
                      modalProps.onClose();
                    },
                    onError: (error) => {
                      logError('AudioListTab:uploadAndCreateGreetingMutation | Failed to upload greeting', {
                        data: { mailboxId, tenantId: tenantLocation.phoneTenantId, error },
                      });
                    },
                  }
                );
              }}
              isUploading={uploadAndCreateGreeting.isLoading}
              modalProps={modalProps}
              setActiveTab={setActiveTab}
            />
          </Tabs.Panel>
          <Tabs.Panel controller='generate'>
            <GenerateAudioModalComponent
              modalProps={modalProps}
              customUploadFn={(payload) => {
                if (!tenantLocation.phoneTenantId) return;
                uploadAndCreateGreeting.mutate(
                  {
                    mailboxId,
                    greetingName: payload.fileName,
                    greetingNumber: 0,
                    file: payload.file,
                  },
                  {
                    onError: (error) => {
                      logError(
                        'GenerateAudioModalComponent:uploadAndCreateGreetingMutation | Failed to upload greeting',
                        {
                          data: { mailboxId, tenantId: tenantLocation.phoneTenantId, error },
                        }
                      );
                    },
                  }
                );
              }}
            />
          </Tabs.Panel>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

const AudioListTab = ({
  tenantLocation,
  mailboxId,
  modalProps,
}: {
  tenantLocation: SettingsTenantLocation;
  mailboxId: string;
  modalProps: ModalControlModalProps;
}) => {
  const { t } = useTranslation('phone');
  const searchField = useFormField({ type: 'text' });

  const { data: phoneMedia, isLoading } = useAudioQuery({
    tenantId: tenantLocation.phoneTenantId ?? '',
    allowedOptions: { custom: true },
    mailboxId,
  });
  // const { data: phoneMedia = [], isLoading } = useQuery({
  //   /**
  //    * TODO: Change this to use the tenant ID instead of the location ID
  //    * We need to use the locationID for now because the endpoint is location based, switching locations in the
  //    */
  //   queryKey: [tenantLocation.locationID, ...queryKeys.settings.listVoicemailGreetings()],
  //   queryFn: () => {
  //     return PhoneMediaApi.list({ locationId: tenantLocation.locationID });
  //   },
  // });
  const { create } = useVoicemailBoxGreetingMutations(tenantLocation.phoneTenantId ?? '', mailboxId);
  const alerts = useAlert();

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const filteredList = useMemo(
    () =>
      phoneMedia?.customMedia
        ? phoneMedia.customMedia.filter((media) => media.name.toLowerCase().includes(searchField.value.toLowerCase()))
        : [],
    [phoneMedia, searchField.value]
  );

  useEffect(() => {
    const mediaId = phoneMedia?.customMedia[activeIndex]?.id;
    if (mediaId) {
      (document.querySelector(`[data-list-id="${mediaId}"]`) as HTMLElement | undefined)?.focus();
    }
  }, [activeIndex, phoneMedia]);

  return (
    <div style={{ display: 'grid', gap: theme.spacing(2) }}>
      <SearchField {...searchField} name='audio-search' />
      {isLoading ? (
        <div style={{ display: 'grid', justifyItems: 'center' }}>
          <SpinningLoader />
          <Text>{t('Loading Audio Files')}</Text>
        </div>
      ) : (
        // This outer div gives the tab a fixed height
        <div
          style={{
            height: 400,
          }}
        >
          {filteredList.length > 0 ? (
            <NakedUl
              css={{
                display: 'grid',
                gap: theme.spacing(1),
                outline: 'none',
                overflow: 'auto',
                maxHeight: 400,
              }}
              onKeyDown={(e) => {
                if (e.code === 'ArrowDown') {
                  setActiveIndex((index) => (index !== null ? Math.min(filteredList.length - 1, index + 1) : 0));
                } else if (e.code === 'ArrowUp') {
                  setActiveIndex((index) => (index !== null ? Math.max(0, index - 1) : 0));
                }
              }}
            >
              {filteredList?.map((item, index) => (
                <li
                  key={item.id}
                  css={[
                    {
                      display: 'grid',
                      gridTemplateColumns: '1fr 40%',
                      gap: theme.spacing(2),
                      alignItems: 'center',
                      border: `1px solid ${theme.colors.neutral30}`,
                      borderRadius: theme.borderRadius.medium,
                      padding: theme.spacing(1),
                      cursor: 'pointer',
                      ':focus-within': {
                        outline: `2px solid ${theme.colors.primary50}`,
                        outlineOffset: -2,
                        background: theme.colors.primary5,
                      },
                    },
                    index === activeIndex &&
                      selectedIndex === activeIndex && {
                        '&,:focus-within': {
                          outline: `2px solid ${theme.colors.primary50}`,
                          outlineOffset: -2,
                          background: theme.colors.white,
                        },
                      },
                    index === selectedIndex && {
                      '&,:focus-within': {
                        outline: `2px solid ${theme.colors.primary50}`,
                        outlineOffset: -2,
                      },
                    },
                  ]}
                  onMouseDown={() => {
                    setActiveIndex(index);
                    setSelectedIndex(index);
                  }}
                  onKeyDown={(e) => {
                    if (e.code === 'Enter' || e.code === 'Space') {
                      setSelectedIndex(index);
                    }
                  }}
                  data-list-id={item.id}
                  tabIndex={index === activeIndex ? 0 : -1}
                >
                  <Text css={styles.truncate}>{getNameWithoutExtension(item.name)}</Text>
                  <CachedAudioScrubber
                    tabIndex={index === activeIndex ? 0 : -1}
                    filePath={buildAudioLibraryPath({
                      media: { id: item.id, path: item.path, isGlobal: false },
                    })}
                    mediaId={item.id}
                  />
                </li>
              ))}
            </NakedUl>
          ) : (
            <div>
              <Text>{t('No audio files found.')}</Text>
            </div>
          )}
        </div>
      )}
      <div
        style={{
          // Just to give the button focus state some space
          marginBottom: theme.spacing(1),
        }}
      >
        {selectedIndex !== null ? (
          <PrimaryButton
            disabled={selectedIndex === null}
            onClick={() => {
              if (selectedIndex !== null && tenantLocation.phoneTenantId) {
                const selectedAudio = filteredList[selectedIndex];
                if (selectedAudio) {
                  create.mutate(
                    {
                      mailboxId,
                      greetingName: selectedAudio.name ?? '',
                      greetingNumber: 0,
                      mediaId: selectedAudio.id,
                      tenantId: tenantLocation.phoneTenantId,
                    },
                    {
                      onSuccess: () => {
                        alerts.success(t('Successfully added greeting.'));
                      },
                      onError: (error) => {
                        alerts.error(t('Failed to add greeting.'));
                        logError('AudioListTab:createMutation | Failed to add greeting', {
                          data: { mailboxId, mediaId: selectedAudio.id, error },
                        });
                      },
                      onSettled: () => {
                        modalProps.onClose();
                      },
                    }
                  );
                }
              }
            }}
          >
            {t('Use Selected Audio')}
          </PrimaryButton>
        ) : (
          <div>
            <Heading level={2} textAlign='center'>
              {t("Don't see your media file?")}
            </Heading>
            <Text>
              {t(
                'Dial *86 on your phone to record a new media file, then refresh this page to have it appear here for selection.'
              )}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const GreetingNumberField = ({
  options,
  greeting,
  onChange,
}: {
  options: { value: string; label: string; disabled: boolean }[];
  greeting: VoicemailGreeting;
  onChange: (incoming: { id: string; number: number; name: string }) => void;
}) => {
  const field = useFormField(
    {
      type: 'dropdown',
      value: greeting.greetingNumber.toString(),
    },
    [greeting.greetingNumber]
  );

  return (
    <DropdownField
      {...field}
      onChange={(e) => {
        field.onChange(e);
        onChange({ id: greeting.greetingId, number: +e.value, name: greeting.greetingName ?? '' });
      }}
      name='greeting-number'
      label=''
    >
      {options.map((option) => {
        return (
          <DropdownField.Option key={option.value} value={option.value}>
            {option.label}
          </DropdownField.Option>
        );
      })}
    </DropdownField>
  );
};

const GreetingNameField = ({
  greeting,
  onChange,
}: {
  greeting: VoicemailGreeting;
  onChange: (incoming: { id: string; number: number; name: string }) => void;
}) => {
  const field = useFormField(
    {
      type: 'text',
      value: greeting.greetingName ?? '',
    },
    [greeting.greetingName]
  );

  return (
    <TextField
      {...field}
      onChange={(e) => {
        field.onChange(e);
        // @ts-ignore
        onChange({ id: greeting.greetingId, number: greeting.greetingNumber, name: e.target.value ?? '' });
      }}
      name='greeting-name'
      label=''
    />
  );
};
